import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, MessageSquare, BarChart, LogOut, Trash2, AlertCircle, Pencil } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { motion, AnimatePresence } from 'framer-motion';

interface User {
  id: string;
  email: string;
  role: string;
  isActive: boolean;
  created_at: string;
  last_sign_in_at?: string;
}

export function AdminDashboard() {
  const { user: authUser, signOut } = useAuthStore(); // Renamed user to authUser to avoid conflict with map variable
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null); // For general errors like fetching users
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // State for Create User Modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('user'); // Default role
  const [newIsActive, setNewIsActive] = useState(true); // Default isActive
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState<string | null>(null);

  // State for Edit User Modal
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('user');
  const [editIsActive, setEditIsActive] = useState(true);
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [updateUserError, setUpdateUserError] = useState<string | null>(null);

  const handleUpdateUser = async () => {
    if (!editingUser) {
      setUpdateUserError("No user selected for editing.");
      return;
    }
    if (!editEmail.trim()) {
      setUpdateUserError('Email cannot be empty.');
      return;
    }

    setIsUpdatingUser(true);
    setUpdateUserError(null);
    const token = localStorage.getItem('authToken');

    if (!token) {
      setUpdateUserError('Authentication token not found. Please log in again.');
      setIsUpdatingUser(false);
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`/api/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: editEmail,
          role: editRole,
          isActive: editIsActive,
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || `Failed to update user: ${response.statusText}`);
      }

      // Success
      await fetchUsers(); // Refresh user list
      setIsEditModalOpen(false); // Close modal
      setEditingUser(null); // Clear editing user state

    } catch (err: any) {
      setUpdateUserError(err.message || 'An unexpected error occurred while updating user.');
    } finally {
      setIsUpdatingUser(false);
    }
  };

  const handleCreateUser = async () => {
    // Basic client-side validation
    if (!newEmail.trim() || !newPassword.trim()) {
      setCreateUserError('Email and Password cannot be empty.');
      return;
    }
    if (newPassword.length < 6) {
      setCreateUserError('Password must be at least 6 characters long.');
      return;
    }

    setIsCreatingUser(true);
    setCreateUserError(null);
    const token = localStorage.getItem('authToken');

    if (!token) {
      setCreateUserError('Authentication token not found. Please log in again.');
      setIsCreatingUser(false); // Should be setIsCreatingUser
      navigate('/login');
      return;
    }

    try {
      const response = await fetch('/api/admin/users', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newEmail,
          password: newPassword,
          role: newRole,
          isActive: newIsActive,
        }),
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || `Failed to create user: ${response.statusText}`);
      }

      // Success
      await fetchUsers(); // Refresh user list
      setIsCreateModalOpen(false); // Close modal
      // Reset form fields (already done when opening, but good for safety if modal isn't always destroyed)
      setNewEmail('');
      setNewPassword('');
      setNewRole('user');
      setNewIsActive(true);

    } catch (err: any) {
      setCreateUserError(err.message || 'An unexpected error occurred while creating user.');
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleOpenEditModal = (userToEdit: User) => {
    setEditingUser(userToEdit);
    setEditEmail(userToEdit.email);
    setEditRole(userToEdit.role);
    setEditIsActive(userToEdit.isActive);
    setIsEditModalOpen(true);
    setUpdateUserError(null); // Clear previous edit errors
  };

  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null); // Clear general fetch error
    const token = localStorage.getItem('authToken');

    if (!token) {
      setError('Authentication token not found. Please log in again.');
      setIsLoading(false);
      navigate('/login'); // Redirect to login if no token
      return;
    }

    try {
      const response = await fetch('/api/admin/users', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to fetch users: ${response.statusText}`);
      }

      const data = await response.json();
      setUsers(data.users || []); // Assuming the backend returns { users: User[] }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred.');
      setUsers([]); // Clear users on error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []); // Removed navigate from dependency array as it's stable

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleDeleteUser = async (userId: string) => {
    setError(null);
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete user');
      }
      // On successful deletion, filter out the user from the local state
      setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred while deleting user.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <div className="flex items-center gap-4">
              <span className="text-gray-600">{authUser?.email}</span>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <LogOut size={18} />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <StatsCard
            title="Total Users"
            value={users.length.toString()}
            icon={Users}
            trend={`${users.length > 0 ? '+' : ''}${users.length}`}
          />
          <StatsCard
            title="Active Users"
            value={users.filter(u => u.isActive).length.toString()}
            icon={MessageSquare}
            trend={users.filter(u => u.isActive).length > 0 ? `+${users.filter(u => u.isActive).length}` : "0"}
          />
          <StatsCard
            title="Admin Users"
            value={users.filter(u => u.role === 'admin').length.toString()}
            icon={BarChart}
            trend="+3%"
          />
        </div>

        {/* Users List */}
        <section className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                User Management
              </h2>
              <button
                onClick={() => {
                  setIsCreateModalOpen(true);
                  setCreateUserError(null); // Clear previous errors when opening
                  // Reset form fields for new entry
                  setNewEmail('');
                  setNewPassword('');
                  setNewRole('user');
                  setNewIsActive(true);
                }}
                className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 flex items-center gap-2"
              >
                <Users size={18} /> Create New User 
              </button>
            </div>
            
            {/* General error display for fetchUsers */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-center gap-2"
              >
                <AlertCircle size={20} />
                {error}
              </motion.div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Sign In
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500"> {/* Increased colspan to 6 */}
                        Loading users...
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500"> {/* Increased colspan to 6 */}
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((currentUser) => ( // Renamed user to currentUser to avoid conflict with authStore.user
                      <tr key={currentUser.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{currentUser.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            currentUser.role === 'admin' 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {currentUser.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            currentUser.isActive 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {currentUser.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(currentUser.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {currentUser.last_sign_in_at 
                            ? new Date(currentUser.last_sign_in_at).toLocaleDateString()
                            : 'Never'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2"> {/* Added flex container */}
                            <button
                              onClick={() => handleOpenEditModal(currentUser)}
                              className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                            >
                              <Pencil size={16} />
                              Edit
                            </button>
                            {deleteConfirm === currentUser.id ? (
                              <> {/* Use fragment for multiple buttons in conditional render */}
                                <button
                                  onClick={() => handleDeleteUser(currentUser.id)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(null)}
                                  className="text-gray-600 hover:text-gray-900"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirm(currentUser.id)}
                                className="text-red-600 hover:text-red-900 flex items-center gap-1"
                              >
                                <Trash2 size={16} />
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </main>

      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setCreateUserError(null); // Clear errors on close
        }}
        onSubmit={handleCreateUser}
        email={newEmail} setEmail={setNewEmail}
        password={newPassword} setPassword={setNewPassword}
        role={newRole} setRole={setNewRole}
        isActive={newIsActive} setIsActive={setNewIsActive}
        isLoading={isCreatingUser}
        error={createUserError}
      />

      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setUpdateUserError(null);
          setEditingUser(null);
        }}
        onSubmit={handleUpdateUser}
        email={editEmail} setEmail={setEditEmail}
        role={editRole} setRole={setEditRole}
        isActive={editIsActive} setIsActive={setEditIsActive}
        isLoading={isUpdatingUser}
        error={updateUserError}
      />
    </div>
  );
}

interface StatsCardProps {
  title: string;
  value: string;
  icon: React.ComponentType<{ size?: number | string; className?: string }>;
  trend: string;
}

function StatsCard({ title, value, icon: Icon, trend }: StatsCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-6 rounded-lg shadow"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className="w-12 h-12 bg-brand-50 rounded-lg flex items-center justify-center">
          <Icon size={24} className="text-brand-600" />
        </div>
      </div>
      <div className="mt-4">
        <span className="text-sm text-green-600">{trend}</span>
        <span className="text-sm text-gray-600 ml-2">vs last month</span>
      </div>
    </motion.div>
  );
}

// Create User Modal Component
function CreateUserModal({
  isOpen,
  onClose,
  onSubmit,
  email, setEmail,
  password, setPassword,
  role, setRole,
  isActive, setIsActive,
  isLoading,
  error
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  email: string; setEmail: (val: string) => void;
  password: string; setPassword: (val: string) => void;
  role: string; setRole: (val: string) => void;
  isActive: boolean; setIsActive: (val: boolean) => void;
  isLoading: boolean;
  error: string | null;
}) {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose} 
    >
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -50, opacity: 0 }}
        className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()} 
      >
        <h3 className="text-xl font-semibold mb-4">Create New User</h3>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="new-email">Email</label>
            <input
              type="email"
              id="new-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="new-password">Password</label>
            <input
              type="password"
              id="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500"
              required
              minLength={6} 
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="new-role">Role</label>
            <select
              id="new-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
              />
              <span className="ml-2 text-sm text-gray-700">Is Active</span>
            </label>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50 disabled:bg-brand-400 flex items-center justify-center"
            >
              {isLoading ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : 'Create User'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// Edit User Modal Component
function EditUserModal({
  isOpen,
  onClose,
  onSubmit,
  email, setEmail,
  role, setRole,
  isActive, setIsActive,
  isLoading,
  error
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  email: string; setEmail: (val: string) => void;
  role: string; setRole: (val: string) => void;
  isActive: boolean; setIsActive: (val: boolean) => void;
  isLoading: boolean;
  error: string | null;
}) {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -50, opacity: 0 }}
        className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-xl font-semibold mb-4">Edit User</h3>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="edit-email">Email</label>
            <input
              type="email"
              id="edit-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="edit-role">Role</label>
            <select
              id="edit-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
              />
              <span className="ml-2 text-sm text-gray-700">Is Active</span>
            </label>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50 disabled:bg-brand-400 flex items-center justify-center"
            >
              {isLoading ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : 'Save Changes'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}